import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { ApiError } from '@/lib/api'
import { SummaryBody, useConversationSummary, useMaySummarise } from './ConversationSummary'
import { fetchTranscript, requestTranscript, type CallTranscript as Transcript } from './summaryApi'

/**
 * Расшифровка звонка в ленте и резюме по ней (тикет #130).
 *
 * Расшифровка читается по нажатию, как и запись (#122): это слова клиента, то есть его ПДн,
 * и лента из двадцати звонков не должна загружать двадцать расшифровок, которые никто не
 * открывал. Распознавание идёт минутами, поэтому после «Расшифровать» состояние
 * переспрашивается, пока строка в очереди.
 */

const POLL_MS = 5000

export function CallTranscript({ activityId }: { activityId: string }) {
  const { t } = useApp()
  const may = useMaySummarise()
  const [transcript, setTranscript] = useState<Transcript | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [summaryOpen, setSummaryOpen] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  const settle = (next: Transcript) => {
    if (!alive.current) return
    setTranscript(next)
    if (timer.current) clearTimeout(timer.current)
    if (next.status === 'pending' || next.status === 'submitted') {
      timer.current = setTimeout(() => {
        fetchTranscript(activityId).then(settle).catch(fail)
      }, POLL_MS)
    }
  }
  const fail = (failure: unknown) => {
    if (!alive.current) return
    setError(failure instanceof ApiError ? failure.message : t('app.error'))
  }
  const run = (action: () => Promise<Transcript>) => {
    setLoading(true)
    setError(null)
    action()
      .then(settle)
      .catch(fail)
      .finally(() => alive.current && setLoading(false))
  }

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          setOpen(true)
          run(() => fetchTranscript(activityId))
        }}
      >
        {t('transcript.show')}
      </button>
    )
  }

  const status = transcript?.status
  return (
    <span className="stack" style={{ gap: 4 }}>
      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)}>
          {t('transcript.hide')}
        </button>
        {may && (status === 'none' || status === 'rejected') && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={loading}
            onClick={() => run(() => requestTranscript(activityId))}
          >
            {status === 'rejected' ? t('transcript.retry') : t('transcript.button')}
          </button>
        )}
        {may && status === 'succeeded' && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setSummaryOpen(true)}>
            {t('summary.callButton')}
          </button>
        )}
      </span>
      {loading && !transcript && <span className="t-sm muted">{t('transcript.loading')}</span>}
      {error && <span className="t-sm" role="alert">{error}</span>}
      {(status === 'pending' || status === 'submitted') && (
        <span className="t-sm muted" role="status">{t(`transcript.state.${status}`)}</span>
      )}
      {status === 'failed' && (
        <span className="t-sm" role="status">{t('transcript.state.failed', { error: transcript?.error ?? '' })}</span>
      )}
      {status === 'rejected' && (
        <span className="t-sm" role="alert">{t('transcript.state.rejected', { error: transcript?.error ?? '' })}</span>
      )}
      {status === 'succeeded' && (
        <pre className="t-sm" style={{ whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>
          {transcript?.transcript || t('transcript.empty')}
        </pre>
      )}
      {summaryOpen && <CallSummary activityId={activityId} />}
    </span>
  )
}

/** Резюме одного звонка: заказывается сразу при открытии, ссылки ведут на фрагменты записи. */
function CallSummary({ activityId }: { activityId: string }) {
  const { summary, error, start } = useConversationSummary('call', activityId, false)
  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    start()
  }, [start])
  return <SummaryBody summary={summary} error={error} />
}
